import IssueFinancialDocumentFormModal from 'src/features/financialDocuments/components/forms/IssueFinancialDocumentFormModal';
import { financialDocumentsMessages } from 'src/features/financialDocuments/utils/messages';
import { Button } from 'src/registry/ui/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICON_MAP } from 'src/utils/constants';
import {
    FINANCIAL_DOCUMENT_STATUS,
    FINANCIAL_DOCUMENT_TYPE,
    FinancialDocument,
} from 'src/features/financialDocuments/utils/types';
import RegenerateFinancialDocumentFormModal from 'src/features/financialDocuments/components/forms/RegenerateFinancialDocumentFormModal';
import UpdateFinancialDocumentFormModal from 'src/features/financialDocuments/components/forms/UpdateFinancialDocumentFormModal';
import { commonMessages, generalMessages } from 'src/utils/messages';
import DeleteFinancialDocumentFormModal from 'src/features/financialDocuments/components/forms/DeleteFinancialDocumentFormModal';
import { useIntl } from 'react-intl';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';

type FinancialDocumentDashboardActionButtonsProps = {
    financialDocument?: FinancialDocument;
};

const FinancialDocumentDashboardActionButtons = ({
    financialDocument,
}: FinancialDocumentDashboardActionButtonsProps) => {
    const { formatMessage } = useIntl();
    const permissionManageFinancialDocs = useHasPermissions([PERMISSION.MANAGE_FINANCIAL_DOCUMENTS]);
    const permissionIssueFinancialDocs = useHasPermissions([PERMISSION.ISSUE_FINANCIAL_DOCUMENTS]);

    const showDeleteActions = financialDocument && financialDocument.status === FINANCIAL_DOCUMENT_STATUS.DRAFT;
    const showPreviewButton =
        financialDocument &&
        financialDocument?.status !== FINANCIAL_DOCUMENT_STATUS.DRAFT &&
        financialDocument?.document_file &&
        financialDocument?.type !== FINANCIAL_DOCUMENT_TYPE.RECEIPT;

    const showIssueButton = showDeleteActions && financialDocument?.type !== FINANCIAL_DOCUMENT_TYPE.RECEIPT;
    return (
        <div className="flex flex-row gap-2">
            {permissionIssueFinancialDocs && showIssueButton && (
                <IssueFinancialDocumentFormModal
                    selectedFinancialDocument={financialDocument}
                    semantics={{
                        entryName: formatMessage(financialDocumentsMessages.financialDocument).toLowerCase(),
                    }}
                >
                    <Button size="mdIcon" variant="default" className="cursor-pointer">
                        <FontAwesomeIcon icon={ICON_MAP.INVOICE} />
                        <span className="hidden md:block">{formatMessage(financialDocumentsMessages.issue)}</span>
                    </Button>
                </IssueFinancialDocumentFormModal>
            )}

            {showPreviewButton && (
                <Button
                    className="cursor-pointer"
                    onClick={() => {
                        window.open(financialDocument.document_file, '_blank', 'noopener,noreferrer');
                    }}
                >
                    <FontAwesomeIcon icon={ICON_MAP.VIEW} />
                    <span className="hidden md:block">{formatMessage(financialDocumentsMessages.preview)}</span>
                </Button>
            )}

            {financialDocument?.status === FINANCIAL_DOCUMENT_STATUS.ISSUED &&
                financialDocument.type !== FINANCIAL_DOCUMENT_TYPE.RECEIPT && (
                    <RegenerateFinancialDocumentFormModal
                        selectedFinancialDocument={financialDocument}
                        semantics={{
                            entryName: formatMessage(financialDocumentsMessages.financialDocument).toLowerCase(),
                        }}
                    >
                        <Button size="mdIcon" variant="default" className="cursor-pointer">
                            <FontAwesomeIcon icon={ICON_MAP.INVOICE} />
                            <span className="hidden md:block">
                                {formatMessage(financialDocumentsMessages.regenerate)}
                            </span>
                        </Button>
                    </RegenerateFinancialDocumentFormModal>
                )}

            {permissionManageFinancialDocs && financialDocument && (
                <UpdateFinancialDocumentFormModal
                    selectedFinancialDocument={financialDocument}
                    semantics={{
                        entryName: formatMessage(financialDocumentsMessages.financialDocument).toLowerCase(),
                    }}
                >
                    <Button size="mdIcon" variant="outline" className="cursor-pointer">
                        <FontAwesomeIcon icon={ICON_MAP.EDIT} />
                        <span className="hidden md:block">{formatMessage(commonMessages.edit)}</span>
                    </Button>
                </UpdateFinancialDocumentFormModal>
            )}
            {permissionManageFinancialDocs && showDeleteActions && (
                <DeleteFinancialDocumentFormModal
                    selectedFinancialDocument={financialDocument}
                    semantics={{
                        entryName: formatMessage(financialDocumentsMessages.financialDocument).toLowerCase(),
                    }}
                >
                    <Button size="mdIcon" variant="destructiveLight" className="cursor-pointer">
                        <FontAwesomeIcon icon={ICON_MAP.DELETE} />
                        <span className="hidden md:block">{formatMessage(generalMessages.delete)}</span>
                    </Button>
                </DeleteFinancialDocumentFormModal>
            )}
        </div>
    );
};

export default FinancialDocumentDashboardActionButtons;
